import {
  FormProvider,
  type DefaultValues,
  type FieldValues,
  type SubmitErrorHandler,
  type SubmitHandler,
  type UseFormReturn,
  useForm,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { ZodType, ZodTypeAny } from 'zod'
import type { CSSProperties, ReactNode } from 'react'
import { extractShape, FormSchemaContext } from './formSchemaContext'

export type FormProps<T extends FieldValues> = {
  schema: ZodTypeAny
  defaultValues?: DefaultValues<T>
  onSubmit: SubmitHandler<T>
  onError?: SubmitErrorHandler<T>
  /**
   * When validation triggers:
   * - 'onBlur' (default): validate when a field loses focus — best UX for most forms
   * - 'onChange': live validation as the user types
   * - 'onSubmit': only validate on submit — good for simple one-step forms
   * - 'onTouched': validate on blur the first time, then on change
   * - 'all': validate on both change and blur
   */
  mode?: 'onBlur' | 'onChange' | 'onSubmit' | 'onTouched' | 'all'
  children: ReactNode | ((methods: UseFormReturn<T>) => ReactNode)
  style?: CSSProperties
  className?: string
}

export const Form = <T extends FieldValues>({
  schema,
  defaultValues,
  onSubmit,
  onError,
  mode = 'onBlur',
  children,
  style,
  className,
}: FormProps<T>) => {
  const methods = useForm<T>({
    resolver: zodResolver(schema as ZodType<T, T>),
    defaultValues,
    mode,
  })

  return (
    <FormSchemaContext.Provider value={extractShape(schema)}>
      <FormProvider {...methods}>
        <form
          onSubmit={(event) => {
            void methods.handleSubmit(onSubmit, onError)(event)
          }}
          style={style}
          className={className}
          noValidate
        >
          {typeof children === 'function' ? children(methods) : children}
        </form>
      </FormProvider>
    </FormSchemaContext.Provider>
  )
}
